import { useEffect, useState } from 'react'
import { ApiClientError } from '../../api/client'
import { getProjectTokenSavings, type TokenSavingsReport } from '../../api/token-savings'
import TokenSavingsSummary from './TokenSavingsSummary'

/**
 * Compact, project-scoped Token Savings panel (Task 7). Manager-only:
 * callers must gate rendering on `canManage` themselves (viewers get no
 * panel at all rather than a forbidden request/error state).
 */
export default function ProjectTokenSavingsPanel({ projectId }: { projectId: number }) {
  const [report, setReport] = useState<TokenSavingsReport | null>(null)
  const [loading, setLoading] = useState(true)
  const [forbidden, setForbidden] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    setForbidden(false)
    getProjectTokenSavings(projectId, { hours: 24 })
      .then((r) => {
        if (!cancelled) setReport(r)
      })
      .catch((err) => {
        if (cancelled) return
        if (err instanceof ApiClientError && err.status === 403) {
          setForbidden(true)
        } else {
          setError(err instanceof Error ? err.message : String(err))
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [projectId])

  if (forbidden) return null

  return (
    <div className="bg-harbor-surface border border-harbor-border rounded-xl">
      <div className="p-6 border-b border-harbor-border">
        <h2 className="text-lg font-semibold text-harbor-text">Token savings</h2>
        <p className="text-sm text-harbor-text-secondary mt-1">Total for this project — last 24h</p>
      </div>
      <div className="p-6">
        {loading && (
          <div className="flex items-center justify-center py-6">
            <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-emerald-500" />
          </div>
        )}
        {!loading && error && (
          <div className="rounded-lg border border-red-800 bg-red-950/40 px-4 py-3 text-sm text-red-300">
            {error}
          </div>
        )}
        {!loading && !error && report && <TokenSavingsSummary report={report} />}
      </div>
    </div>
  )
}
